import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RoomFieldCombo } from "./RoomFieldCombo";
import { Loader2, AlertTriangle, ArrowLeft, Plus, Layers } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Searchable room-information fields (offered from what the FRS already uses).
const COMBO_FIELDS = [
  ["building", "Building"], ["floor", "Floor"], ["area", "Area"],
  ["room_number", "Room #"], ["room_name", "Room name"], ["room_type", "Room type"],
];
// Room information shared by every room in a batch (the per-room identity is entered per row).
const SHARED_COMBO_FIELDS = COMBO_FIELDS.filter(([f]) => !["room_number", "room_name"].includes(f));
// The rest of the FRS room information, typed free.
const TEXT_FIELDS = [
  ["floor_plan", "Floor plan"], ["plan_sheet", "Enlarged floor plan"],
  ["elevation_sheet", "Elevations"], ["num_rooms", "#Units"], ["room_notes", "Room notes"],
];
const BLANK = {
  building: "", floor: "", area: "", room_number: "", room_name: "", room_type: "",
  floor_plan: "", plan_sheet: "", elevation_sheet: "", num_rooms: "", room_notes: "",
};
const MAX_ROOMS = 50;
const norm = (v) => String(v || "").trim().toLowerCase();
const blankRow = () => ({ room_number: "", room_name: "", decision: "", attach_to: null });

/**
 * Add Room — quantity → scope → room identification → products.
 * Nothing is ever copied, matched or suggested from another room, room type,
 * area, floor or building. A batch of N rooms is N ordinary room creations: the
 * same request, the same validation, the same save, run once per room, so every
 * room ends up an independent record.
 */
export default function AddRoomScopeWizard({ open, onOpenChange, projectId, scopes = [],
                                             fieldOptions = {}, role, actor, onDone,
                                             // FT-3a — in the grid the wizard does not write: it
                                             // hands back `add_room` ops that join the pending
                                             // batch, so a new room is saved with everything else.
                                             onStage = null }) {
  const [step, setStep] = useState("count");        // count | scope | rooms | products
  const [count, setCount] = useState("1");
  const [room, setNewRoom] = useState({ ...BLANK });   // the single-room form / shared fields
  const [rows, setRows] = useState([blankRow()]);      // per-room identity for a batch
  const [scope, setScope] = useState("");
  const [existing, setExisting] = useState([]);        // every room already in the FRS
  const [wantProducts, setWantProducts] = useState(null);   // null | false | true
  const [catalogue, setCatalogue] = useState([]);
  const [lines, setLines] = useState({});             // product_uid → qty (string)
  const [dupRooms, setDupRooms] = useState(null);     // room number already in use
  const [newProduct, setNewProduct] = useState(null); // { product_code, product_description, unit_of_measure }
  const [dupProduct, setDupProduct] = useState(false); // code already used — next save forces
  const [busy, setBusy] = useState(false);

  const total = Math.min(Math.max(parseInt(count, 10) || 1, 1), MAX_ROOMS);
  const batch = total > 1;

  useEffect(() => {
    if (!open) return;
    setStep("count"); setCount("1"); setNewRoom({ ...BLANK }); setRows([blankRow()]);
    setScope(""); setWantProducts(null); setCatalogue([]); setLines({});
    setDupRooms(null); setNewProduct(null); setDupProduct(false); setExisting([]);
  }, [open]);

  // What the FRS already carries, so a duplicate can be caught before anything is saved.
  useEffect(() => {
    if (!open || !projectId) return;
    api.get("/frs-editor/rooms", { params: { project_id: projectId, limit: 50000 } })
      .then(({ data }) => setExisting(data || []))
      .catch(() => setExisting([]));
  }, [open, projectId]);

  // The scope's own product list — quantities always start empty.
  useEffect(() => {
    if (step !== "products" || !wantProducts || !scope) return;
    api.get("/frs-editor/products", { params: { project_id: projectId, scope_type: scope } })
      .then(({ data }) => setCatalogue(data || []))
      .catch(() => setCatalogue([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, wantProducts, scope, projectId]);

  // A ticked product counts even at a blank/0 quantity — the room still gets
  // the column, ready for the crew to fill in later.
  const picked = Object.entries(lines)
    .map(([product_uid, q]) => ({ product_uid, quantity: Math.max(parseInt(q, 10) || 0, 0) }));
  const pickedLabels = picked.map((l) => {
    const p = catalogue.find((c) => c.product_uid === l.product_uid);
    return `${p?.label || l.product_uid} × ${l.quantity}`;
  });

  const setRow = (i, patch) => setRows((cur) =>
    cur.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // Keep the row count in step with the quantity the user asked for.
  const openRooms = () => {
    setRows((cur) => Array.from({ length: total },
      (_, i) => cur[i] || blankRow()).slice(0, total));
    setStep("rooms");
  };

  /** Per-row verdict: blank, duplicated inside the batch, already on this scope,
   *  or the same number sitting on another scope (which is a question, not an error). */
  const verdicts = useMemo(() => rows.map((r, i) => {
    const number = norm(r.room_number);
    const name = norm(r.room_name);
    if (!number && !name) return { state: "blank" };
    const twin = rows.findIndex((o, j) => j !== i
      && ((number && norm(o.room_number) === number) || (!number && name && norm(o.room_name) === name)));
    if (twin > -1 && twin < i) return { state: "batch_duplicate" };
    const hits = existing.filter((e) => (number && norm(e.room_number) === number)
      || (!number && name && norm(e.room_name) === name));
    if (!hits.length) return { state: "ok" };
    const sameScope = hits.filter((h) => h.scope_type === scope);
    if (sameScope.length) return { state: "scope_duplicate", rooms: sameScope };
    return { state: "other_scope", rooms: hits };
  }), [rows, existing, scope]);

  const roomsReady = verdicts.every((v, i) => v.state === "ok"
    || (v.state === "other_scope" && rows[i].decision));

  const addProduct = async (force = false) => {
    const p = newProduct;
    if (!p?.product_code?.trim()) { toast.error("Give the product a code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/frs-editor/product-records", {
        project_id: projectId, scope_type: scope, product_code: p.product_code.trim(),
        product_description: p.product_description || "", unit_of_measure: p.unit_of_measure || "Each",
        role, actor, force,
      });
      const uid = data.product_uid;
      const { data: list } = await api.get("/frs-editor/products",
        { params: { project_id: projectId, scope_type: scope } });
      setCatalogue(list || []);
      if (uid) setLines((cur) => ({ ...cur, [uid]: "1" }));
      setNewProduct(null);
      setDupProduct(false);
      toast.success(`${p.product_code} added to ${scope}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "product_exists" && d.product?.product_uid) {
        // Already registered — just select it.
        setLines((cur) => ({ ...cur, [d.product.product_uid]: "1" }));
        setNewProduct(null);
        toast.info(d.message || "That product already exists — selected it for you");
      } else if (d?.error === "possible_duplicate") {
        toast.warning(`${d.message} Press "Add product" again to add it anyway.`);
        setDupProduct(true);
      } else {
        toast.error(typeof d === "string" ? d : d?.message || "Could not add that product");
      }
    }
    setBusy(false);
  };

  /** One room = one ordinary create. A batch just runs this per row. */
  const createRoom = (identity, force) => api.post("/frs-editor/rooms", {
    project_id: projectId, ...identity, scope_types: [scope],
    products: wantProducts ? picked : [], role, actor, force,
  });

  const save = async (force = false) => {
    setBusy(true);
    try {
      if (onStage) {
        const identities = batch
          ? rows.map((r) => {
              const attach = r.decision === "attach" ? (r.attach_to || {}) : {};
              return {
                ...room,
                building: attach.building ?? room.building,
                floor: attach.floor ?? room.floor,
                area: attach.area ?? room.area,
                room_type: attach.room_type ?? room.room_type,
                room_number: r.room_number.trim(),
                room_name: r.room_name.trim() || (attach.room_name || ""),
              };
            })
          : [room];
        onStage(identities.map((id) => ({
          op: "add_room", scope_type: scope,
          room: { project_id: projectId, building: id.building || "", floor: String(id.floor || ""),
                  area: id.area || "", room_number: String(id.room_number || ""),
                  room_type: id.room_type || "" },
          room_number: String(id.room_number || ""), room_name: id.room_name || "",
          room_type: id.room_type || "", rr_type: "",
          fields: Object.fromEntries(TEXT_FIELDS.map(([f]) => [f, id[f] || ""])
                                               .filter(([, v]) => v !== "")),
          products: wantProducts ? picked.map((l) => ({ product_uid: l.product_uid,
                                                        qty: l.quantity })) : [],
        })), { scope, count: identities.length });
        onOpenChange(false);
        setBusy(false);
        return;
      }
      if (!batch) {
        const { data } = await createRoom(room, force);
        toast.success(`Room ${room.room_number || room.room_name} added to ${scope}`
          + ` — ${data.created_rows} line(s)`);
        onDone?.();
        onOpenChange(false);
        setBusy(false);
        return;
      }
      // Duplicates were already answered row by row, so each create is forced on purpose.
      const done = [];
      const failed = [];
      for (let i = 0; i < rows.length; i += 1) {
        const r = rows[i];
        const attach = r.decision === "attach" ? (r.attach_to || {}) : {};
        const identity = {
          ...room,
          building: attach.building ?? room.building,
          floor: attach.floor ?? room.floor,
          area: attach.area ?? room.area,
          room_type: attach.room_type ?? room.room_type,
          room_number: r.room_number.trim(),
          room_name: r.room_name.trim() || (attach.room_name || ""),
        };
        try {
          // eslint-disable-next-line no-await-in-loop
          await createRoom(identity, true);
          done.push(identity.room_number || identity.room_name);
        } catch (e) {
          const d = e.response?.data?.detail;
          failed.push(`${identity.room_number || identity.room_name}: `
            + (typeof d === "string" ? d : d?.message || "rejected"));
        }
      }
      if (done.length) {
        toast.success(`${done.length} room(s) added to ${scope} — ${done.join(", ")}`);
      }
      if (failed.length) {
        toast.error(`${failed.length} room(s) were not created. ${failed[0]}`);
      }
      onDone?.();
      if (!failed.length) onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.existing_rooms?.length) setDupRooms(d.existing_rooms);
      else toast.error(typeof d === "string" ? d : d?.message || "That change was rejected");
    }
    setBusy(false);
  };

  const back = () => setStep(step === "products" ? "rooms" : step === "rooms" ? "scope" : "count");

  const title = step === "count" ? "How many rooms do you want to add?"
    : step === "scope" ? "Which scope is being added?"
    : step === "rooms" ? (batch ? `Name the ${total} rooms` : "New room information")
    : "Would you like to add products?";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="add-room-wizard">
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription className="text-xs">
            {step === "count" ? "One at a time, or up to 50 in a batch."
              : step === "scope" ? `${total} room(s)`
              : step === "rooms" ? (batch
                ? "Room number or name is required on every row. Everything else is shared."
                : "Everything is entered fresh.")
              : batch ? `These products will be applied to all ${total} rooms`
              : `Room ${room.room_number || room.room_name || "—"}`}
          </DialogDescription>
        </DialogHeader>

        {step === "count" && (
          <div className="space-y-3">
            <div className="flex items-end gap-3">
              <div>
                <Label className="text-[10px] uppercase text-gray-500">Rooms to add</Label>
                <Input
                  autoFocus type="number" min="1" max={MAX_ROOMS} value={count}
                  data-testid="wizard-room-count"
                  onChange={(e) => setCount(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && setStep("scope")}
                  className="h-9 w-28 text-sm"
                />
              </div>
              <p className="pb-1 text-xs text-gray-500" data-testid="wizard-count-hint">
                {batch
                  ? `You will name each of the ${total} rooms, then pick products once for all of them.`
                  : "Leave it at 1 for a single room."}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wizard-cancel">
                Cancel
              </Button>
              <Button variant="nav" onClick={() => setStep("scope")} data-testid="wizard-count-next">
                Continue
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "scope" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {scopes.map((s) => (
                <button key={s} type="button" onClick={() => setScope(s)}
                        data-testid={`wizard-scope-${s}`}
                        className={`h-11 rounded-lg border text-sm font-semibold ${
                          scope === s ? "bg-blue-600 text-white border-blue-700"
                                      : "bg-white text-gray-700 border-gray-300"}`}>
                  {s}
                </button>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={back} data-testid="wizard-back">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back
              </Button>
              <Button variant="nav" disabled={!scope} onClick={openRooms} data-testid="wizard-scope-next">
                {batch ? `Next — name the ${total} rooms` : "Next — room information"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "rooms" && !batch && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {COMBO_FIELDS.map(([f, label]) => (
                <RoomFieldCombo
                  key={f} field={f} label={label} value={room[f] || ""}
                  testid={`wizard-room-${f}`}
                  options={fieldOptions[f] || []}
                  required={f === "room_number"} optional={f !== "room_number"}
                  typeFirst={["room_number", "room_name"].includes(f)}
                  placeholder={f === "room_number" ? "" : "— leave blank —"}
                  onChange={(v) => setNewRoom({ ...room, [f]: v })}
                />
              ))}
              {TEXT_FIELDS.map(([f, label]) => (
                <div key={f}>
                  <Label className="text-[10px] uppercase text-gray-500">
                    {label}<span className="normal-case text-gray-400"> · optional</span>
                  </Label>
                  <Input value={room[f]} onChange={(e) => setNewRoom({ ...room, [f]: e.target.value })}
                         data-testid={`wizard-room-${f}`} className="h-8 text-xs"
                         placeholder="— leave blank —" />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={back} data-testid="wizard-back">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back
              </Button>
              <Button variant="nav" disabled={!room.room_number.trim()} onClick={() => setStep("products")}
                      data-testid="wizard-details-next">Next — products</Button>
            </DialogFooter>
          </div>
        )}

        {step === "rooms" && batch && (
          <div className="space-y-3">
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto"
                 data-testid="wizard-room-rows">
              {rows.map((r, i) => {
                const v = verdicts[i];
                const bad = ["batch_duplicate", "scope_duplicate"].includes(v.state);
                return (
                  <div key={i} className="px-3 py-2" data-testid={`wizard-room-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-6 text-[11px] text-gray-400">{i + 1}</span>
                      <Input value={r.room_number} placeholder="Room #"
                             data-testid={`wizard-row-number-${i}`}
                             onChange={(e) => setRow(i, { room_number: e.target.value,
                                                          decision: "", attach_to: null })}
                             className={`h-8 w-28 text-xs ${bad ? "border-red-400" : ""}`} />
                      <Input value={r.room_name} placeholder="Room name"
                             data-testid={`wizard-row-name-${i}`}
                             onChange={(e) => setRow(i, { room_name: e.target.value,
                                                          decision: "", attach_to: null })}
                             className={`h-8 flex-1 text-xs ${bad ? "border-red-400" : ""}`} />
                      {v.state === "ok" && <span className="text-[10px] text-emerald-600">ready</span>}
                      {v.state === "blank" && <span className="text-[10px] text-gray-400">needed</span>}
                    </div>

                    {v.state === "batch_duplicate" && (
                      <p className="pl-8 pt-1 text-[11px] text-red-600"
                         data-testid={`wizard-row-batch-dup-${i}`}>
                        You have already used that room in this batch.
                      </p>
                    )}
                    {v.state === "scope_duplicate" && (
                      <p className="pl-8 pt-1 text-[11px] text-red-600"
                         data-testid={`wizard-row-scope-dup-${i}`}>
                        Room {r.room_number || r.room_name} is already in {scope}. Change it.
                      </p>
                    )}
                    {v.state === "other_scope" && (
                      <div className="pl-8 pt-1" data-testid={`wizard-row-other-scope-${i}`}>
                        <p className="text-[11px] text-amber-800">
                          Room {r.room_number || r.room_name} already exists on{" "}
                          {[...new Set(v.rooms.map((x) => x.scope_type))].join(", ")}. Add {scope} to
                          that room, or create a separate room for {scope}?
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          <Button size="sm" variant={r.decision === "attach" ? "default" : "outline"}
                                  className="h-6 text-[11px]"
                                  data-testid={`wizard-row-attach-${i}`}
                                  onClick={() => setRow(i, { decision: "attach",
                                                             attach_to: v.rooms[0] })}>
                            Add {scope} to that room
                          </Button>
                          <Button size="sm" variant={r.decision === "new" ? "default" : "outline"}
                                  className="h-6 text-[11px]"
                                  data-testid={`wizard-row-new-${i}`}
                                  onClick={() => setRow(i, { decision: "new", attach_to: null })}>
                            Create a new room
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="rounded-lg border border-gray-200 p-2">
              <p className="pb-1 text-[10px] uppercase tracking-wide text-gray-500">
                Shared by all {total} rooms · optional
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {SHARED_COMBO_FIELDS.map(([f, label]) => (
                  <RoomFieldCombo
                    key={f} field={f} label={label} value={room[f] || ""}
                    testid={`wizard-shared-${f}`}
                    options={fieldOptions[f] || []}
                    optional
                    placeholder="— leave blank —"
                    onChange={(v) => setNewRoom({ ...room, [f]: v })}
                  />
                ))}
                {TEXT_FIELDS.map(([f, label]) => (
                  <div key={f}>
                    <Label className="text-[10px] uppercase text-gray-500">
                      {label}<span className="normal-case text-gray-400"> · optional</span>
                    </Label>
                    <Input value={room[f]} className="h-8 text-xs" placeholder="— leave blank —"
                           data-testid={`wizard-shared-${f}`}
                           onChange={(e) => setNewRoom({ ...room, [f]: e.target.value })} />
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={back} data-testid="wizard-back">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back
              </Button>
              <Button variant="nav" disabled={!roomsReady} onClick={() => setStep("products")}
                      data-testid="wizard-rooms-next">
                Next — products for all {total}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "products" && (
          <div className="space-y-3">
            {batch && (
              <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900"
                   data-testid="wizard-batch-notice">
                <Layers className="w-3.5 h-3.5 shrink-0" />
                These products will be applied to all {total} rooms. Afterwards each room is its
                own record — editing one room's products later leaves the others alone.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => { setWantProducts(false); setLines({}); }}
                      data-testid="wizard-products-no"
                      className={`h-11 rounded-lg border text-sm font-semibold ${
                        wantProducts === false ? "bg-gray-900 text-white border-gray-900"
                                               : "bg-white text-gray-700 border-gray-300"}`}>
                No — finish without products
              </button>
              <button type="button" onClick={() => setWantProducts(true)}
                      data-testid="wizard-products-yes"
                      className={`h-11 rounded-lg border text-sm font-semibold ${
                        wantProducts === true ? "bg-blue-600 text-white border-blue-700"
                                              : "bg-white text-gray-700 border-gray-300"}`}>
                Yes — add products
              </button>
            </div>

            {wantProducts && (
              <>
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100"
                     data-testid="wizard-product-list">
                  {catalogue.map((p) => {
                    const on = lines[p.product_uid] !== undefined;
                    return (
                      <div key={p.product_uid} className="flex items-center gap-3 px-3 py-2"
                           data-testid={`wizard-product-${p.product_uid}`}>
                        <input
                          type="checkbox" className="w-4 h-4" checked={on}
                          data-testid={`wizard-product-check-${p.product_uid}`}
                          onChange={() => setLines((cur) => {
                            const n = { ...cur };
                            if (on) delete n[p.product_uid]; else n[p.product_uid] = "";
                            return n;
                          })}
                        />
                        <span className="flex-1 min-w-0 text-sm truncate" title={p.label}>{p.label}</span>
                        <Input
                          type="number" min="0" disabled={!on} placeholder="qty"
                          value={on ? lines[p.product_uid] : ""}
                          onChange={(e) => setLines({ ...lines, [p.product_uid]: e.target.value })}
                          data-testid={`wizard-product-qty-${p.product_uid}`}
                          className="h-8 w-20 text-sm"
                        />
                      </div>
                    );
                  })}
                  {catalogue.length === 0 && (
                    <div className="px-3 py-4 text-sm text-gray-500" data-testid="wizard-product-empty">
                      No products registered in {scope} yet — add one below.
                    </div>
                  )}
                </div>

                {newProduct ? (
                  <div className="border border-blue-200 bg-blue-50/60 rounded-lg p-2 grid grid-cols-3 gap-2"
                       data-testid="wizard-new-product-form">
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">Code</Label>
                      <Input autoFocus value={newProduct.product_code} className="h-8 text-xs"
                             data-testid="wizard-new-product-code"
                             onChange={(e) => setNewProduct({ ...newProduct, product_code: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">Description</Label>
                      <Input value={newProduct.product_description} className="h-8 text-xs"
                             data-testid="wizard-new-product-desc"
                             onChange={(e) => setNewProduct({ ...newProduct, product_description: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">Unit</Label>
                      <Input value={newProduct.unit_of_measure} className="h-8 text-xs"
                             data-testid="wizard-new-product-unit"
                             onChange={(e) => setNewProduct({ ...newProduct, unit_of_measure: e.target.value })} />
                    </div>
                    <div className="col-span-3 flex gap-2">
                      <Button size="sm" disabled={busy} onClick={() => addProduct(dupProduct)} data-testid="wizard-new-product-save">
                        {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                        {dupProduct ? "Add anyway" : "Add product"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setNewProduct(null); setDupProduct(false); }}
                              data-testid="wizard-new-product-cancel">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" data-testid="wizard-add-product-btn"
                          onClick={() => setNewProduct({ product_code: "", product_description: "", unit_of_measure: "Each" })}>
                    <Plus className="w-3 h-3 mr-1" /> Add a new product to {scope}
                  </Button>
                )}

                <div className="text-xs text-gray-500" data-testid="wizard-products-summary">
                  {picked.length} product(s) · {picked.reduce((s, l) => s + l.quantity, 0)} unit(s)
                </div>
              </>
            )}

            {wantProducts !== null && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700"
                   data-testid="wizard-confirm-summary">
                <p className="font-semibold text-gray-900">Before you save</p>
                <p data-testid="wizard-confirm-rooms">
                  {total} room(s) into {scope}:{" "}
                  {(batch ? rows.map((r) => r.room_number || r.room_name)
                          : [room.room_number || room.room_name]).join(", ") || "—"}
                </p>
                <p data-testid="wizard-confirm-products">
                  {wantProducts
                    ? (pickedLabels.length ? pickedLabels.join(" · ") : "no products selected yet")
                    : "no products — room information only"}
                </p>
                {batch && rows.some((r) => r.decision === "attach") && (
                  <p data-testid="wizard-confirm-attach">
                    {rows.filter((r) => r.decision === "attach").length} of these add {scope} to a
                    room that already exists on another scope.
                  </p>
                )}
              </div>
            )}

            {dupRooms && (
              <div className="text-xs border border-amber-300 bg-amber-50 rounded p-2"
                   data-testid="wizard-duplicate-room">
                <div className="flex items-center gap-1.5 font-semibold text-amber-900">
                  <AlertTriangle className="w-3.5 h-3.5" /> Room {room.room_number} already exists
                </div>
                <ul className="mt-1 list-disc pl-5 text-amber-900">
                  {dupRooms.map((r) => (
                    <li key={r.room_key}>{r.label} — carries {r.scopes.join(", ") || "no scope"}</li>
                  ))}
                </ul>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" disabled={busy} data-testid="wizard-duplicate-force"
                          onClick={() => { setDupRooms(null); save(true); }}>
                    Add it anyway
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setDupRooms(null); setStep("rooms"); }}
                          data-testid="wizard-duplicate-edit">Change the room number</Button>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={back} data-testid="wizard-back">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back
              </Button>
              <Button
                disabled={busy || !!dupRooms || wantProducts === null
                          || (wantProducts && picked.length === 0)}
                onClick={() => save(false)} data-testid="wizard-finish">
                {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                {batch
                  ? `${onStage ? "Add" : "Create"} ${total} rooms`
                  : onStage ? "Add to pending"
                  : wantProducts ? `Save ${picked.length} product(s)` : "Create room"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
